import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
const identifier = z.string().min(1).max(120).regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)*$/);
export const assistantRedactionProfileKeySchema = z.string()
    .min(2)
    .max(80)
    .regex(/^[a-z][a-z0-9-]*$/);
function validBlockedPath(path) {
    if (!path.startsWith('/') || path === '/')
        return false;
    return path.slice(1).split('/').every((segment) => segment.length > 0 &&
        !/[\u0000-\u001f\u007f]/.test(segment) &&
        /^(?:[^~]|~0|~1)+$/.test(segment));
}
export const assistantBlockedPathSchema = z.string().min(2).max(512).refine(validBlockedPath, 'Use a JSON-pointer path such as /customer/email; * matches one segment and ** matches descendants.');
export const assistantAllowedFactTypesSchema = z.array(identifier.or(z.literal('*'))).max(200);
export const assistantBlockedPathsSchema = z.array(assistantBlockedPathSchema).max(200);
export const assistantRedactionProfileSchema = z.strictObject({
    allowedFactTypes: assistantAllowedFactTypesSchema,
    blockedPaths: assistantBlockedPathsSchema,
});
export const assistantRedactionPolicyValueSchema = z.strictObject({
    defaultProfile: assistantRedactionProfileKeySchema,
    profiles: z.record(assistantRedactionProfileKeySchema, assistantRedactionProfileSchema)
        .refine((profiles) => Object.keys(profiles).length > 0, 'At least one redaction profile is required.'),
}).superRefine((policy, context) => {
    if (!(policy.defaultProfile in policy.profiles)) {
        context.addIssue({ code: 'custom', path: ['defaultProfile'], message: 'The default profile must exist.' });
    }
});
const assistantRedactionPolicyV1ValueSchema = z.strictObject({
    defaultProfile: assistantRedactionProfileKeySchema,
    profiles: z.record(assistantRedactionProfileKeySchema, z.strictObject({
        allowedFactTypes: assistantAllowedFactTypesSchema,
        blockedFields: z.array(z.string().min(1).max(120)).max(200),
    })).refine((profiles) => Object.keys(profiles).length > 0),
});
function escapeJsonPointerSegment(value) {
    return value.replaceAll('~', '~0').replaceAll('/', '~1');
}
export function assistantMigrateRedactionPolicyV1(value) {
    const policy = assistantRedactionPolicyV1ValueSchema.parse(value);
    return assistantRedactionPolicyValueSchema.parse({
        defaultProfile: policy.defaultProfile,
        profiles: Object.fromEntries(Object.entries(policy.profiles).map(([key, profile]) => [
            key,
            {
                allowedFactTypes: profile.allowedFactTypes,
                blockedPaths: profile.blockedFields.map((field) => field === '*' ? '/*' : `/${escapeJsonPointerSegment(field)}`),
            },
        ])),
    });
}
export const assistantRedactionPolicySetting = definePlatformSetting({
    definition: {
        key: 'assistant.context.redaction-policy',
        moduleId: 'assistant',
        title: 'Assistant context redaction policy',
        helpText: 'Controls tenant-approved Assistant context fact types and fields before context reaches an Assistant consumer.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 2,
        codedFallback: {
            defaultProfile: 'default',
            profiles: {
                default: {
                    allowedFactTypes: ['*'],
                    blockedPaths: ['/**/email', '/**/phone', '/**/address', '/**/notes'],
                },
                strict: { allowedFactTypes: [], blockedPaths: ['/*'] },
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'replace',
        clientSafe: false,
        serverOnly: true,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: false,
        safetyClass: 'protected',
    },
    valueSchema: assistantRedactionPolicyValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: assistantMigrateRedactionPolicyV1,
        }],
});
export const assistantWorkspaceDisplayValueSchema = z.strictObject({
    historyLayout: z.enum(['sidebar', 'stacked']),
    density: z.enum(['compact', 'comfortable']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    maximumMessageWidthPx: z.number().int().min(320).max(1_200),
    historyWidthPx: z.number().int().min(220).max(480),
    showProviderIdentity: z.boolean(),
    showTimestamps: z.boolean(),
});
export const assistantWorkspaceDisplaySetting = definePlatformSetting({
    definition: {
        key: 'assistant.display.workspace', moduleId: 'assistant', title: 'Assistant workspace display',
        helpText: 'Controls conversation layout, density, card size, message width, and visible provider metadata. Colors, typography, and card geometry inherit the tenant Platform theme.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            historyLayout: 'sidebar', density: 'comfortable', cardSize: 'standard',
            maximumMessageWidthPx: 760, historyWidthPx: 300,
            showProviderIdentity: true, showTimestamps: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: true, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: assistantWorkspaceDisplayValueSchema,
});
const assistantQuickActionSchema = z.strictObject({
    label: z.string().trim().min(1).max(80),
    prompt: z.string().trim().min(1).max(500),
});
export const assistantWorkspaceCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(120),
    description: z.string().trim().min(1).max(300),
    historyLabel: z.string().trim().min(1).max(80),
    untitledConversationLabel: z.string().trim().min(1).max(80),
    newConversationAction: z.string().trim().min(1).max(80),
    emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(400),
    composerLabel: z.string().trim().min(1).max(120),
    composerPlaceholder: z.string().trim().min(1).max(200),
    sendAction: z.string().trim().min(1).max(80),
    cancelAction: z.string().trim().min(1).max(80),
    retryAction: z.string().trim().min(1).max(80),
    queuedLabel: z.string().trim().min(1).max(160),
    runningLabel: z.string().trim().min(1).max(160),
    unavailableTitle: z.string().trim().min(1).max(120),
    failedTitle: z.string().trim().min(1).max(120),
    cancelledTitle: z.string().trim().min(1).max(120),
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    loadingLabel: z.string().trim().min(1).max(160),
    quickActions: z.array(assistantQuickActionSchema).max(8),
});
export const assistantWorkspaceCopySetting = definePlatformSetting({
    definition: {
        key: 'assistant.copy.workspace', moduleId: 'assistant', title: 'Assistant workspace copy',
        helpText: 'Controls tenant-editable Assistant navigation, headings, status text, actions, composer labels, and quick prompts.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Assistant', title: 'CREST Assistant',
            description: 'Ask questions using only the CREST information you are authorized to access.',
            historyLabel: 'Your conversations', untitledConversationLabel: 'Conversation',
            newConversationAction: 'New conversation',
            emptyTitle: 'How can I help?',
            emptyDescription: 'Start a conversation or choose a common question. Changes to CRM records always use a separate review and approval path.',
            composerLabel: 'Message', composerPlaceholder: 'Ask CREST Assistant…', sendAction: 'Send',
            cancelAction: 'Cancel response', retryAction: 'Retry response',
            queuedLabel: 'Your request is queued.', runningLabel: 'CREST Assistant is preparing a response.',
            unavailableTitle: 'Assistant provider unavailable', failedTitle: 'Response failed',
            cancelledTitle: 'Response cancelled', loadError: 'Conversation history could not be loaded.',
            mutationError: 'The Assistant request could not be saved. Your draft is still available.',
            loadingLabel: 'Loading Assistant…',
            quickActions: [
                { label: 'My schedule today', prompt: 'Summarize my schedule today.' },
                { label: 'Open tasks', prompt: 'Summarize my open tasks.' },
                { label: 'Project status', prompt: 'Summarize the current Project status.' },
            ],
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: assistantWorkspaceCopyValueSchema,
});
export const assistantSettingRegistrations = [
    assistantRedactionPolicySetting,
    assistantWorkspaceDisplaySetting,
    assistantWorkspaceCopySetting,
];
//# sourceMappingURL=assistant-setting-definitions.js.map